import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import './styles.css';
import { Providers } from './app/providers';
import { createAppRouter } from './app/router';
import { loadLocale } from './i18n/load';
import { holdPreloadsUntil } from '@kidova/ui-web';
import { appReady, markAppReady, whenHydrated } from './lib/hydration';
import { onSessionExpired } from './lib/api';
import { authHint, currentLocale } from './lib/preferences';
import { clearCache, createQueryClient, persistCache, restoreCache } from './lib/query-client';

const queryClient = createQueryClient();
// Synchronous restore before the first render: cached pages paint complete in frame one.
restoreCache(queryClient);
persistCache(queryClient);
const router = createAppRouter(queryClient);

onSessionExpired(() => {
  authHint.set(false);
  clearCache(queryClient);
  const next = router.state.location.href;
  void router.navigate({ to: '/login', search: { next: next === '/' ? undefined : next } });
});

await loadLocale(currentLocale());

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');
const root = document.documentElement;
// §22.3: parent dashboard first load (cold, 4G) ≤ 2.5 s.
const FIRST_LOAD_BUDGET_MS = 2500;

// Menus, dialogs and the toaster load their code only after the first screen (lib/hydration.ts).
holdPreloadsUntil(appReady);
const stopWatching = router.subscribe('onRendered', () => {
  markAppReady();
  stopWatching();
});

const app = (
  <StrictMode>
    <Providers client={queryClient}>
      <RouterProvider router={router} />
    </Providers>
  </StrictMode>
);

// Prerendered public pages (scripts/prerender.ts) are hydrated in place once their route code is
// loaded, so the markup the parent already sees is kept, not replaced. A page prerendered in
// another language than the parent's (boot.js hid it) is rendered fresh instead.
if (container.hasChildNodes() && !root.hasAttribute('data-locale-mismatch')) {
  await router.load();
  // Text typed before the app arrived lives only in the DOM; the hydrated inputs start from their
  // prerendered (empty) state, so it is handed to React as ordinary input once hydration is done.
  const typed = [
    ...container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'),
  ]
    .filter((el) => el.type !== 'checkbox' && el.type !== 'radio' && el.value !== el.defaultValue)
    .map((el) => [el, el.value] as const);
  whenHydrated(() => {
    for (const [el, value] of typed) {
      const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement : HTMLInputElement;
      // React only reports a change against the value it last saw: reset that, then set the
      // text underneath it, so the input event reaches onChange.
      el.value = '';
      Object.getOwnPropertyDescriptor(proto.prototype, 'value')?.set?.call(el, value);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
    root.removeAttribute('data-hydrating');
    markAppReady();
  });
  hydrateRoot(container, app);
} else {
  root.removeAttribute('data-hydrating');
  // The first frame shows the whole page (shell, route and its data, fetched early by boot.js)
  // instead of the shell first and the page a moment later. If that takes longer than the §22.3
  // first-load budget, the shell and skeletons appear at the budget instead of a blank screen.
  const budget = Math.max(0, FIRST_LOAD_BUDGET_MS - performance.now());
  await Promise.race([router.load(), new Promise((resolve) => setTimeout(resolve, budget))]);
  container.replaceChildren();
  createRoot(container).render(app);
}

// Transitions switch on only after the first painted frame (see [data-booting] in styles.css).
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    root.removeAttribute('data-booting');
    root.removeAttribute('data-locale-mismatch');
  });
});
